import React from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Typography } from '@esgian/esgianui';
import { ProfileLink } from '@components/Links';
import { tableTitlePaddingLeft } from '@helpers';
import { useTheme } from '@hooks';
import TableV2 from '@components/Tables/TableV2';
import moment from 'moment';
import { useTimezone } from '@hooks/useTimezone';

function FuturePortCallsTable({ loading = false, futurePortCalls = [], name = '' }) {
  const { theme } = useTheme();
  const { getTimeZoneDateDisplay } = useTimezone();
  const location = useLocation();

  const columns = [
    {
      name: 'vesselName',
      label: 'Vessel',
      options: {
        filter: true,
        customFilterType: 'multiselect',
        sort: true,
        display: location.pathname.includes('/vessel/') ? 'false' : 'true',
        sortThirdClickReset: true,
        customBodyRender: (vesselName) => {
          const match = futurePortCalls.find((call) => call.vesselName === vesselName);
          const imo = match?.imo;
          return vesselName !== null ? (
            <ProfileLink profile={'vessel'} name={vesselName} id={imo} />
          ) : (
            '--'
          );
        },
        customDownloadValue: (value) => {
          return value;
        }
      }
    },
    {
      name: 'imo',
      label: 'IMO Number',
      options: {
        sort: true,
        filter: true,
        display: location.pathname.includes('/vessel/') ? 'false' : 'true',
        download: !location.pathname.includes('/vessel/'),
        customFilterType: 'textField'
      }
    },
    {
      name: 'portName',
      label: 'Port',
      options: {
        sort: true,
        filter: true,
        display: true,
        download: true,
        sortThirdClickReset: true,
        customFilterType: 'multiselect',
        customBodyRender: (portName) => {
          const match = futurePortCalls.find((call) => call.portName === portName);
          const portId = match?.portId;
          return portName !== null ? (
            <ProfileLink profile={'port'} name={portName} id={portId} />
          ) : (
            '--'
          );
        },
        customDownloadValue: (value) => {
          return value;
        }
      }
    },
    {
      name: 'arriving',
      label: 'Arrival',
      options: {
        display: true,
        download: true,
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customFilterType: 'dateBetween',
        customBodyRender: (dataIndex) => {
          if (moment(dataIndex).isValid()) {
            return getTimeZoneDateDisplay(dataIndex);
          }
          return '-';
        }
      }
    },
    {
      name: 'departing',
      label: 'Departure',
      options: {
        display: true,
        download: true,
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customFilterType: 'dateBetween',
        customBodyRender: (dataIndex) => {
          if (moment(dataIndex).isValid()) {
            return getTimeZoneDateDisplay(dataIndex);
          }
          return '-';
        }
      }
    },
    {
      name: 'lastUpdated',
      label: 'Last updated',
      options: {
        display: true,
        download: true,
        filter: true,
        sort: true,
        sortThirdClickReset: true,
        customFilterType: 'dateBetween',
        customBodyRender: (dataIndex) => {
          if (moment(dataIndex).isValid()) {
            return getTimeZoneDateDisplay(dataIndex);
          }
          return '-';
        }
      }
    }
  ];

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    filter: false,
    search: false,
    download: false,
    setTableProps: () => {
      return {
        id: 'vessel-port-calls-table'
      };
    },
    downloadOptions: {
      filename: `Upcoming-Port-Calls-${name}.csv`
    },
    print: false,
    viewColumns: false,
    pagination: true,
    toolbar: false,
    elevation: 0,
    tableBodyMaxHeight: '80vh',
    rowsPerPageOptions: [10, 25, 50, 100],
    sortOrder: {
      name: 'arriving',
      direction: 'asc'
    }
  };
  return (
    <TableV2
      customStyle={tableTitlePaddingLeft}
      loading={loading}
      mode={theme.mode}
      title={
        <Typography id={'vessel-port-calls-title'} variant={'h6'}>
          Scheduled Vessel Port Calls
        </Typography>
      }
      data={
        futurePortCalls?.length
          ? futurePortCalls.filter((call) => call.vesselName && call.portName)
          : []
      }
      columns={columns}
      options={options}
    />
  );
}

FuturePortCallsTable.propTypes = {
  loading: PropTypes.bool,
  name: PropTypes.string,
  assetLevel: PropTypes.oneOf(['country', 'vessel', 'operator', 'owner', 'port', 'region'])
    .isRequired,
  futurePortCalls: PropTypes.arrayOf(PropTypes.object)
};

FuturePortCallsTable.defaultProps = {
  futurePortCalls: [],
  titleWithTooltip: false,
  tooltipText: ''
};

export default FuturePortCallsTable;
